import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { PayPoints } from "Components/Paypoints/PayPoints";
import { ExternalIntegrationsSection } from "Components/Paypoints/ExternalIntegrations/ExternalIntegrationsSection";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { isTerminalProductCode } from "Components/Shared/Merchant/Products/AppProducts/constants";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";

const COMPONENT_RESOURCES = Resources.Components.Merchant.PayPoints;

type Props = {
  merchantPublicID?: string;
  isReadOnly?: boolean;
};

export const PayPointsPageContent = ({
  merchantPublicID,
  isReadOnly = false,
}: Props) => {
  const isMerchant = useIsCurrentUserMerchant();
  const { data: bankAccounts, isLoading: isLoadingBankAccounts } =
    useBankingAccounts(undefined, isMerchant);
  const { data: merchantProducts, isLoading: isMerchantProductsLoading } =
    useGetMerchantAppProductsQuery(merchantPublicID);
  const { t } = useResource();

  if (isMerchantProductsLoading || isLoadingBankAccounts) {
    return <CenteredCircularProgress />;
  }

  if (!merchantProducts?.length) {
    return <Typography>{t(COMPONENT_RESOURCES.NoProductAssigned)}</Typography>;
  }

  const hasBankAccount = (bankAccounts?.items.length ?? 0) > 0;
  const productsWithPaypoints = merchantProducts.filter(
    ({ productDto }) =>
      productDto.nfcPayPointsLimit > 0 ||
      isTerminalProductCode(productDto.code),
  );

  if (!productsWithPaypoints.length) {
    return <Typography>{t(COMPONENT_RESOURCES.NoProductAssigned)}</Typography>;
  }

  return (
    <Stack gap={10}>
      {!hasBankAccount && isMerchant && <NoBankAccountWarning />}
      <PayPoints
        products={productsWithPaypoints}
        merchantPublicID={merchantPublicID}
        isReadOnly={isReadOnly}
      />
      <ExternalIntegrationsSection />
    </Stack>
  );
};

const NoBankAccountWarning = () => {
  const { t } = useResource();

  return (
    <PrimaryPaper
      sx={{
        border: "1px solid",
        borderColor: "error.main",
        p: 3,
        borderRadius: 2,
        width: "fit-content",
      }}
    >
      <Stack gap={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <WarningAmberOutlinedIcon
            sx={({ palette }) => ({
              color: palette.error.main,
              width: 20,
              height: 20,
            })}
          />
          <ErrorText fontSize={18} fontWeight={500}>
            {t(COMPONENT_RESOURCES.NoBankAccountConnectedWarning.title)}
          </ErrorText>
        </Box>
        <Typography display="flex" gap={1} flexWrap="wrap">
          {t(COMPONENT_RESOURCES.NoBankAccountConnectedWarning.linkPretext)}
          <UnstyledLink to={appUrl("banking-auth")}>
            {t(COMPONENT_RESOURCES.NoBankAccountConnectedWarning.linkText)}
          </UnstyledLink>
        </Typography>
      </Stack>
    </PrimaryPaper>
  );
};
